import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { debounce } from "@mui/material";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

export interface PlacePrediction {
  placeId: string;
  primaryText: string;
  secondaryText: string;
}

export interface ResolvedPlace {
  lat: number;
  lng: number;
  label: string;
}

interface UsePlacePredictionsOptions {
  center?: google.maps.LatLngLiteral;
  bounds?: google.maps.LatLngBoundsLiteral;
  radiusMeters?: number;
  minLength?: number;
  limit?: number;
}

export interface UsePlacePredictionsResult {
  predictions: PlacePrediction[];
  loading: boolean;
  resolvePlace: (placeId: string) => Promise<ResolvedPlace | null>;
}

type PredictionStatus = Parameters<
  NonNullable<
    Parameters<google.maps.places.AutocompleteService["getPlacePredictions"]>[1]
  >
>[1];

const DEFAULT_RADIUS_METERS = 50000;
const PREDICTION_DEBOUNCE_MS = 300;
const EARTH_RADIUS_METERS = 6371008.8;

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

const distanceMeters = (
  from: google.maps.LatLngLiteral,
  to: google.maps.LatLngLiteral,
) => {
  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLng = toRadians(to.lng - from.lng);
  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) *
      Math.cos(toRadians(to.lat)) *
      Math.sin(deltaLng / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(a)));
};

export const PLACE_QUERY_MIN_LENGTH = 3;

const localPredictionsFirst = (
  results: google.maps.places.AutocompletePrediction[],
  radiusMeters: number,
) => {
  const distance = (result: google.maps.places.AutocompletePrediction) =>
    result.distance_meters ?? Number.POSITIVE_INFINITY;

  return [
    ...results.filter((result) => distance(result) <= radiusMeters),
    ...results.filter((result) => distance(result) > radiusMeters),
  ];
};

export const usePlacePredictions = (
  query: string,
  {
    center,
    bounds,
    radiusMeters = DEFAULT_RADIUS_METERS,
    minLength = PLACE_QUERY_MIN_LENGTH,
    limit = 5,
  }: UsePlacePredictionsOptions = {},
): UsePlacePredictionsResult => {
  const places = useMapsLibrary("places");
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [loading, setLoading] = useState(false);

  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const sessionToken =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  const lat = center?.lat;
  const lng = center?.lng;
  const north = bounds?.north;
  const south = bounds?.south;
  const east = bounds?.east;
  const west = bounds?.west;

  useEffect(() => {
    if (!places) return;

    autocompleteService.current = new places.AutocompleteService();
    placesService.current = new places.PlacesService(
      document.createElement("div"),
    );
    sessionToken.current = new places.AutocompleteSessionToken();
  }, [places]);

  const viewport = useMemo(() => {
    if (
      north === undefined ||
      south === undefined ||
      east === undefined ||
      west === undefined
    ) {
      return undefined;
    }
    const viewportCenter = { lat: (north + south) / 2, lng: (east + west) / 2 };
    return {
      literal: { north, south, east, west },
      center: viewportCenter,
      radiusMeters: distanceMeters(viewportCenter, { lat: north, lng: east }),
    };
  }, [north, south, east, west]);

  const bias = useMemo(() => {
    if (!places) return undefined;
    if (viewport) {
      return { bounds: viewport.literal, origin: viewport.center };
    }
    if (lat === undefined || lng === undefined) return undefined;
    return {
      location: new google.maps.LatLng(lat, lng),
      radius: radiusMeters,
      origin: { lat, lng },
    };
  }, [places, viewport, lat, lng, radiusMeters]);

  const localRadiusMeters = viewport?.radiusMeters ?? radiusMeters;

  const fetchPredictions = useMemo(
    () =>
      debounce(
        (
          request: google.maps.places.AutocompletionRequest,
          callback: (
            results: google.maps.places.AutocompletePrediction[] | null,
            status: PredictionStatus,
          ) => void,
        ) => {
          autocompleteService.current?.getPlacePredictions(request, callback);
        },
        PREDICTION_DEBOUNCE_MS,
      ),
    [],
  );

  useEffect(() => {
    if (!places || query.trim().length < minLength) {
      setPredictions([]);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);

    if (!sessionToken.current) {
      sessionToken.current = new places.AutocompleteSessionToken();
    }

    fetchPredictions(
      {
        input: query,
        sessionToken: sessionToken.current,
        ...bias,
      },
      (results, status) => {
        if (!active) return;
        if (status !== "OK" && status !== "ZERO_RESULTS") {
          console.error("Google Places autocomplete failed:", status);
        }
        const ordered = bias
          ? localPredictionsFirst(results ?? [], localRadiusMeters)
          : (results ?? []);

        setPredictions(
          ordered.slice(0, limit).map((result) => ({
            placeId: result.place_id,
            primaryText: result.structured_formatting.main_text,
            secondaryText: result.structured_formatting.secondary_text ?? "",
          })),
        );
        setLoading(false);
      },
    );

    return () => {
      active = false;
      fetchPredictions.clear();
    };
  }, [
    places,
    query,
    minLength,
    limit,
    bias,
    localRadiusMeters,
    fetchPredictions,
  ]);

  const resolvePlace = useCallback(
    (placeId: string): Promise<ResolvedPlace | null> =>
      new Promise((resolve) => {
        const service = placesService.current;
        if (!places || !service) {
          resolve(null);
          return;
        }

        service.getDetails(
          {
            placeId,
            fields: ["geometry", "name", "formatted_address"],
            ...(sessionToken.current && { sessionToken: sessionToken.current }),
          },
          (place, status) => {
            sessionToken.current = new places.AutocompleteSessionToken();

            const location = place?.geometry?.location;
            if (status !== places.PlacesServiceStatus.OK || !location) {
              resolve(null);
              return;
            }

            resolve({
              lat: location.lat(),
              lng: location.lng(),
              label: place?.formatted_address ?? place?.name ?? "",
            });
          },
        );
      }),
    [places],
  );

  return { predictions, loading, resolvePlace };
};
